// ============================================================================
// Module 12.0: Analytics & AI Engine — Service Client
// ============================================================================

import { apiRequest } from '../../shared/api';
import {
  AnalyticsDashboardKpis,
  AnalyticsDatasetRecord,
  AnalyticsMetricRecord,
  FactSafetyMeasurement,
  AnalyticsTrendDefinition,
  AnalyticsTrendEvent,
  AnalyticsMLModel,
  AnalyticsPrediction,
  AnalyticsAiInsight,
  AnalyticsSnapshot,
  AnalyticsDataQualityCheck,
  AnalyticsSpcReport,
} from './types';

export const analyticsService = {
  async getDashboardKpis(): Promise<AnalyticsDashboardKpis> {
    return apiRequest<AnalyticsDashboardKpis>('/analytics/dashboard/kpis');
  },

  async getDatasets(domain?: string): Promise<AnalyticsDatasetRecord[]> {
    const url = domain ? `/analytics/datasets?domain=${encodeURIComponent(domain)}` : '/analytics/datasets';
    const res = await apiRequest<AnalyticsDatasetRecord[]>(url);
    return Array.isArray(res) ? res : [];
  },

  async getDataQualityChecks(): Promise<AnalyticsDataQualityCheck[]> {
    const res = await apiRequest<AnalyticsDataQualityCheck[]>('/analytics/data-quality');
    return Array.isArray(res) ? res : [];
  },

  async getMetrics(domain?: string): Promise<AnalyticsMetricRecord[]> {
    const url = domain ? `/analytics/metrics?domain=${encodeURIComponent(domain)}` : '/analytics/metrics';
    const res = await apiRequest<AnalyticsMetricRecord[]>(url);
    return Array.isArray(res) ? res : [];
  },

  async createMetric(data: {
    metric_code: string;
    domain: string;
    name: string;
    description?: string;
    unit?: string;
    calculation_formula: string;
    target_direction?: 'MINIMIZE' | 'MAXIMIZE' | 'TARGET_BAND';
    owner_role?: string;
  }): Promise<AnalyticsMetricRecord> {
    return apiRequest<AnalyticsMetricRecord>('/analytics/metrics', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async getMeasurements(metricId?: string): Promise<FactSafetyMeasurement[]> {
    const url = metricId ? `/analytics/measurements?metric_id=${encodeURIComponent(metricId)}` : '/analytics/measurements';
    const res = await apiRequest<FactSafetyMeasurement[]>(url);
    return Array.isArray(res) ? res : [];
  },

  async recordMeasurement(data: {
    metric_id: string;
    measurement_period: string;
    period_start_date: string;
    period_end_date: string;
    metric_value: number;
    numerator_value?: number;
    denominator_value?: number;
    flight_hours?: number;
    flight_cycles?: number;
    source_record_count?: number;
  }): Promise<FactSafetyMeasurement> {
    return apiRequest<FactSafetyMeasurement>('/analytics/measurements', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async getTrendDefinitions(): Promise<AnalyticsTrendDefinition[]> {
    const res = await apiRequest<AnalyticsTrendDefinition[]>('/analytics/trends/definitions');
    return Array.isArray(res) ? res : [];
  },

  async getSpcAnalysis(metricCode?: string, months: number = 24): Promise<AnalyticsSpcReport> {
    const params = new URLSearchParams();
    if (metricCode) params.set('metric_code', metricCode);
    if (months) params.set('months', String(months));
    const url = `/analytics/spc?${params.toString()}`;
    return apiRequest<AnalyticsSpcReport>(url);
  },

  async getTrendEvents(status?: string): Promise<AnalyticsTrendEvent[]> {
    const url = status ? `/analytics/trends/events?status=${encodeURIComponent(status)}` : '/analytics/trends/events';
    const res = await apiRequest<AnalyticsTrendEvent[]>(url);
    return Array.isArray(res) ? res : [];
  },

  async reviewTrendEvent(
    id: string,
    data: {
      status: 'ACKNOWLEDGED' | 'UNDER_REVIEW' | 'MITIGATED' | 'DISMISSED';
      reviewed_by: string;
      review_notes?: string;
    }
  ): Promise<AnalyticsTrendEvent> {
    return apiRequest<AnalyticsTrendEvent>(`/analytics/trends/events/${id}/review`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async getMLModels(): Promise<AnalyticsMLModel[]> {
    const res = await apiRequest<AnalyticsMLModel[]>('/analytics/ml/models');
    return Array.isArray(res) ? res : [];
  },

  async getPredictions(): Promise<AnalyticsPrediction[]> {
    const res = await apiRequest<AnalyticsPrediction[]>('/analytics/ml/predictions');
    return Array.isArray(res) ? res : [];
  },

  async getAiInsights(reviewStatus?: string): Promise<AnalyticsAiInsight[]> {
    const url = reviewStatus ? `/analytics/ai/insights?review_status=${encodeURIComponent(reviewStatus)}` : '/analytics/ai/insights';
    const res = await apiRequest<AnalyticsAiInsight[]>(url);
    return Array.isArray(res) ? res : [];
  },

  async reviewAiInsight(
    id: string,
    data: {
      review_status: 'ACCEPTED' | 'REJECTED' | 'ACTION_CREATED';
      reviewed_by: string;
      review_comments?: string;
    }
  ): Promise<AnalyticsAiInsight> {
    return apiRequest<AnalyticsAiInsight>(`/analytics/ai/insights/${id}/review`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async getSnapshots(): Promise<AnalyticsSnapshot[]> {
    const res = await apiRequest<AnalyticsSnapshot[]>('/analytics/snapshots');
    return Array.isArray(res) ? res : [];
  },

  async createSnapshot(data: {
    snapshot_code: string;
    title: string;
    period: string;
    domain: string;
    snapshot_data: any;
    published_by: string;
  }): Promise<AnalyticsSnapshot> {
    return apiRequest<AnalyticsSnapshot>('/analytics/snapshots', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};
